/**
 * MechaGuard - TowerMenu (底部建造菜单)
 *
 * 从塔注册表动态生成按钮，处理选中态与「买不起」的灰化表现。
 *
 * 注意：买不起时按钮依然可点击（只是变暗），这样 Game 能给出
 * 「金币不足」的提示，而不是让玩家点击后毫无反馈。
 */

import { TOWER_TYPES } from '../tower-types/index.js';
import { balanceConfig } from '../config/BalanceConfig.js';

export class TowerMenu {
  constructor() {
    this.selectedId = null;
    this._gold = Infinity;
    /** @type {Map<string, HTMLButtonElement>} */
    this._buttons = new Map();
    /** @type {((id:string)=>void)|null} */
    this._onSelect = null;
  }

  /**
   * @param {(id:string)=>void} onSelect 点击某个塔按钮的回调
   */
  init(onSelect) {
    this._onSelect = onSelect;

    const container = document.getElementById('tower-menu');
    if (!container) return;

    container.innerHTML = '';
    this._buttons.clear();

    // 默认按生效售价升序排列（售价可被运营在 ?ops=1 调整）
    const sorted = [...TOWER_TYPES].sort((a, b) =>
      balanceConfig.getTowerCost(a.id, a.cost) - balanceConfig.getTowerCost(b.id, b.cost));

    for (const type of sorted) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tower-btn';
      btn.dataset.id = type.id;
      btn.title = this._tipText(type);

      const icon = document.createElement('span');
      icon.className = 'tower-icon';
      icon.textContent = type.icon;

      const name = document.createElement('span');
      name.className = 'tower-name';
      name.textContent = type.name;

      const cost = document.createElement('span');
      cost.className = 'tower-cost';
      cost.textContent = `${this._costOf(type)}G`;

      btn.append(icon, name, cost);

      // 用 pointerdown 而不是 click：移动端响应更快，且不会与
      // canvas 的 pointer 逻辑产生顺序问题
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (this._onSelect) this._onSelect(type.id);
      });

      container.appendChild(btn);
      this._buttons.set(type.id, btn);
    }

    this._apply();
  }

  /** 设置当前选中的塔类型（null 表示未选中） */
  setSelected(id) {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this._apply();
  }

  /** 金币变化时刷新可购买状态 */
  setGold(gold) {
    if (this._gold === gold) return;
    this._gold = gold;
    this._apply();
  }

  /** 兼容旧接口 */
  refresh(gold) {
    if (typeof gold === 'number') this._gold = gold;
    this._apply();
  }

  getSelectedId() {
    return this.selectedId;
  }

  /** 生效售价（可被运营在 ?ops=1 调整） */
  _costOf(type) {
    return balanceConfig.getTowerCost(type.id, type.cost);
  }

  /** 悬浮提示：基于当前配置显示售价与核心数值 */
  _tipText(type) {
    const s = balanceConfig.getTowerStats(type.id, type.stats);
    const dmg = Number(s.baseDamage) || 0;
    const range = Number(s.baseRange) || 0;
    const dps = s.fireInterval > 0 ? dmg / s.fireInterval : 0;
    return `${type.name} · ${type.desc}\n`
      + `售价 ${this._costOf(type)}G\n`
      + `伤害 ${dmg.toFixed(1)} · 射程 ${Math.round(range)} · 每秒 ${dps.toFixed(1)}`;
  }

  /** 配置变动后刷新按钮的售价文案与悬浮提示（并同步可购买状态） */
  syncConfig() {
    for (const type of TOWER_TYPES) {
      const btn = this._buttons.get(type.id);
      if (!btn) continue;
      btn.title = this._tipText(type);
      const cs = btn.querySelector('.tower-cost');
      if (cs) cs.textContent = `${this._costOf(type)}G`;
    }
    this._apply();
  }

  _apply() {
    for (const type of TOWER_TYPES) {
      const btn = this._buttons.get(type.id);
      if (!btn) continue;

      btn.classList.toggle('selected', type.id === this.selectedId);
      btn.classList.toggle('disabled', this._gold < balanceConfig.getTowerCost(type.id, type.cost));
    }
  }
}
